import React, { useState, useEffect } from 'react';

import { Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever,
} from "@mui/icons-material";

import { 
  Condition,
  Potence,
  UpgradableNumber,
} from "@dndModels";
import { 
  CHARACTER_LEVELS,
} from "@dndModels/Constants";
import { ModelBase } from "@models";

import DandDAPI from "@dndUtilities/system_api";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectConditionBox from "@dndComponents/select/SelectConditionBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import CheckBox from "@components/input/CheckBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ModelBaseInput from "@dndComponents/ModelBaseInput";
import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  obj: Potence;
  slot_level: number;
  onChange: (changed: Potence) => void;
  onDelete: () => void;
  disabled?: boolean;
  for_obj?: ModelBase;
}

const api = DandDAPI.getInstance();

export default function AbilityPotenceSelfConditionsInput({ 
  obj,
  slot_level,
  onChange,
  onDelete,
  disabled = false,
  for_obj
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [new_condition, setNewCondition] = useState(new Condition());
  
  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setLoading(true);
      api.getSetOfObjects({ data_types: ["condition"] }).then(res => {
        if (res) {
          setConditions(res.condition as Condition[]);
          setLoading(false);
        }
      });
      setFetched(true);
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const submit = () => {
    setProcessing(true);
    // debugger;
    api.upsertObject("condition", new_condition).then((res: any) => {
      // debugger;
      setProcessing(false);
      if (res && res.body) {
        const new_obj = obj;
        new_obj.conditions_applied.push(res.body.id);
        new_obj.the_conditions = conditions.filter(o => 
          new_obj.conditions_applied.includes(o._id)) as Condition[];
        onChange(new_obj);
      }
      setReloading(true);
      setOpen(false);
    });
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={ open } 
        onClose={() => { setOpen(false); }}>
        <ModelBaseInput 
          obj={new_condition}
          onChange={(obj: ModelBase) => {
            if (obj instanceof Condition) {
              setNewCondition(obj);
            }
          }} 
          processing={processing}
          disabled={false}
          onCancel={() => { setOpen(false); }}
          onSubmit={submit}
        />
      </ModalOrDrawer>
    );
  };

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const cond = obj.conditions_applied.length === 1 ? conditions.find(o => o._id === obj.conditions_applied[0]) : undefined;
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 10
          }}>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox 
              name="Level"
              options={CHARACTER_LEVELS}
              value={`${obj.level}`} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.level = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            { !reloading &&
              <SelectConditionBox
                name="Apply Conditions"
                option_objects={conditions}
                multiple
                vulnerabilities//={main_type === "Debuff"}
                resistances//={main_type === "Buff"}
                immunities//={main_type === "Buff"}
                extra_options={["Add New Condition"]}
                values={obj.conditions_applied}
                disabled={disabled}
                onChange={(changed: string[]) => {
                  if (changed.includes("Add New Condition")) {
                    const nc = new Condition();
                    //nc.condition_type = main_type === "Debuff" ? "Debuff" : (main_type === "Buff" ? "Buff" : "Other");
                    if (for_obj) {
                      nc.name = for_obj.name;
                      nc.description = for_obj.description;
                    }
                    if (api.user && api.user.admin) {
                      api.applyWorkingOn(nc);
                    }
                    setNewCondition(nc);
                    setOpen(true);
                    setReloading(true);
                  } else {
                    const new_obj = obj;
                    new_obj.conditions_applied = changed;
                    new_obj.the_conditions = conditions.filter(o => changed.includes(o._id)) as Condition[];
                    onChange(new_obj);
                  }
                }}
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox 
              name="Extra"
              value={obj.extra} 
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.extra = changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          { !disabled &&
            <Tooltip title={`Delete Potence`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  onDelete();
                }}>
                <DeleteForever/>
              </Fab>
            </Tooltip>
          }
        </Grid>
        { cond && cond.max_level > 1 && 
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <UpgradableNumberBox
                name="Levels"
                value={obj.condition_levels_applied}
                disabled={disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.condition_levels_applied = changed.clone();
                  onChange(new_obj);
                }}
              />
            </Grid> 
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="Remove Condition Levels"
                value={obj.remove_condition_levels} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.remove_condition_levels = changed;
                  onChange(new_obj);
                }}
              />
            </Grid>
          </>
        }
        { renderExtras() }
      </Grid>
    );
  }
}
